import React, { useEffect, useState } from "react";
import { Alert, ScrollView, StyleSheet, Text, View } from "react-native";
import * as Sentry from "@sentry/react-native";

import DiagnosticItemComponent from "./Components/DiagnosticItem";
import SendDiagnosticsButton from "./Components/SendDiagnosticButton";
import { fetchDiagnostics } from "./Services/diagnosticService";
import { useLayoutDiagnostics } from "./Services/utils/fetchScreenInfo";
import { useUpdateInfo } from "./Services/utils/fetchUpdateInfo";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import { toReadableText } from "@/formatting/text";
import {
  DiagnosticCategory,
  type DiagnosticRun,
} from "@/schemas/app/diagnostic";
import { useSession } from "@/context/auth/useSession";

function DiagnosticsScreen(): React.JSX.Element {
  const [diagnosticRun, setDiagnosticRun] = useState<DiagnosticRun | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const layoutDiagnostics = useLayoutDiagnostics();
  const updateDiagnostics = useUpdateInfo();
  const { isSessionLoading, session } = useSession();

  const loadDiagnostics = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const diagnosticRun = await fetchDiagnostics(session);
      diagnosticRun[DiagnosticCategory.LAYOUT] = layoutDiagnostics;
      diagnosticRun[DiagnosticCategory.UPDATE] = updateDiagnostics;
      setDiagnosticRun(diagnosticRun);
    } catch (error) {
      Alert.alert(
        "Error",
        "Failed to load diagnostics. Please try again later.",
        [{ text: "OK" }],
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendDiagnostics = (): void => {
    if (!diagnosticRun) return;

    setIsSending(true);
    try {
      Sentry.captureException(new Error("Diagnostics Report"), {
        extra: diagnosticRun,
      });
      Alert.alert("Diagnostics Sent", "Diagnostics sent successfully.", [
        { text: "OK" },
      ]);
    } catch (error) {
      Alert.alert(
        "Error",
        "Failed to send diagnostics. Please try again later.",
        [{ text: "OK" }],
      );
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    loadDiagnostics();
  }, []);

  return (
    <ScreenLayout isLoading={isLoading || isSessionLoading}>
      <ScrollView style={styles.scrollView}>
        {diagnosticRun &&
          Object.entries(diagnosticRun).map(([category, items]) => (
            <View key={category} style={styles.category}>
              <Text style={styles.categoryTitle}>
                {toReadableText(category)}
              </Text>
              {items.map((item, index) => (
                <DiagnosticItemComponent key={index} item={item} />
              ))}
            </View>
          ))}
      </ScrollView>
      <View style={styles.footer}>
        <SendDiagnosticsButton
          onPress={handleSendDiagnostics}
          isSending={isSending}
        />
      </View>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    padding: 16,
  },
  category: {
    marginBottom: 24,
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#333",
  },
  footer: {
    padding: 16,
    backgroundColor: "#fff",
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
});

export default DiagnosticsScreen;
